import React, { memo, useCallback } from "react";
import { SquarePen, PanelLeftClose, PanelLeftOpen, CircleHelp } from "lucide-react";
import { ConversationItem } from "~/components/assistant/ConversationItem";
import { FeaturedConversationItem } from "~/components/assistant/FeaturedConversationItem";
import { IconButton, PopoverButton } from "~/components/assistant/PopoverButton";
import { ZENDESK_HELP_URL } from "~/components/assistant/constants";
import { useListboxKeyboardNavigation } from "~/components/assistant/hooks/useListboxKeyboardNavigation";
import { Conversation, ConversationShortcut } from "~/components/assistant/types";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { useConversations } from "~/contexts/conversations";
import { cn } from "~/lib/utils";

// Extract conversation ID - defined outside component for stable reference
const getConversationId = (conversation: Conversation) => conversation.id;

interface ChatSidebarProps {
  datasetTitle: string;
  conversations: Conversation[];
  currentConversationId: string | null;
  conversationShortcuts?: ConversationShortcut[];
  onNewChat: () => void;
  onSelect: (conversationId: string) => void;
  onDelete: (conversationId: string, event: React.MouseEvent) => void;
  onRename: (conversationId: string, displayName: string) => void;
  onToggle?: () => void;
  isCollapsed?: boolean;
}

export const Sidebar = memo(function Sidebar({
  conversations,
  currentConversationId,
  conversationShortcuts,
  onNewChat,
  onSelect,
  onDelete,
  onRename,
  onToggle,
  isCollapsed = false,
}: ChatSidebarProps) {
  const visibleShortcuts = conversationShortcuts?.filter((s) => s.url) ?? [];
  const conversationsContext = useConversations();
  const currentConversation = conversations.find((c) => c.id === currentConversationId) || null;

  const handleSelectConversation = useCallback(
    (conversation: Conversation) => onSelect(conversation.id),
    [onSelect]
  );

  const { focusedIndex, handleKeyDown, getFocusedItemId } = useListboxKeyboardNavigation({
    items: conversations,
    onSelect: handleSelectConversation,
    selectedItem: currentConversation,
    getItemId: getConversationId,
    idPrefix: "conversation-",
  });

  const formatConversationLabel = (conversation: Conversation) => {
    // Use display_name if available
    if (conversation.display_name) {
      return conversation.display_name;
    }

    const date = new Date(conversation.created_at);
    const now = new Date();

    const timeString = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    // Check if it's today, yesterday, or another date
    const isToday = date.toDateString() === now.toDateString();
    const isYesterday = (() => {
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      return date.toDateString() === yesterday.toDateString();
    })();

    let dateString: string;

    if (isToday) {
      dateString = "Today";
    } else if (isYesterday) {
      dateString = "Yesterday";
    } else {
      dateString = date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    }

    return `${dateString}, ${timeString}`;
  };

  const handleConversationSelect = (conversationId: string) => {
    onSelect(conversationId);
  };

  const focusedItemId = getFocusedItemId();

  return (
    <div
      className={cn(
        "flex flex-col h-full",
        isCollapsed ? "w-auto" : "w-64 min-w-64",
        isCollapsed ? "" : "border-r border-gray-200"
      )}
      aria-label="Conversation sidebar"
    >
      {/* Header */}
      <div className="flex flex-col gap-1 p-3">
        {/* Collapse/Expand Button */}
        {onToggle && (
          <div className="mb-2">
            <Tooltip delayDuration={700}>
              <TooltipTrigger asChild>
                <IconButton onClick={onToggle} variant="ghost">
                  {isCollapsed ? (
                    <PanelLeftOpen className="size-4.5" />
                  ) : (
                    <PanelLeftClose className="size-4.5" />
                  )}
                </IconButton>
              </TooltipTrigger>
              <TooltipContent>
                <p>{isCollapsed ? "Show recents" : "Hide recents"}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        )}

        {/* New Analysis Button */}
        {isCollapsed ? (
          <Tooltip delayDuration={700}>
            <TooltipTrigger asChild>
              <IconButton
                onClick={onNewChat}
                variant="ghost"
                aria-label="New Analysis"
                className="h-8"
              >
                <SquarePen className="size-4.5" />
              </IconButton>
            </TooltipTrigger>
            <TooltipContent>
              <p>New Analysis</p>
            </TooltipContent>
          </Tooltip>
        ) : (
          <PopoverButton
            onClick={onNewChat}
            variant="ghost"
            aria-label="New Analysis"
            className="h-8 has-[>svg]:px-1.5"
          >
            <SquarePen className="size-4.5" />
            <span className="text-sm font-normal">New Analysis</span>
          </PopoverButton>
        )}

        {/* Usage Guide Button */}
        {isCollapsed ? (
          <Tooltip delayDuration={700}>
            <TooltipTrigger asChild>
              <IconButton
                onClick={() => window.open(ZENDESK_HELP_URL, "_blank", "noopener,noreferrer")}
                variant="ghost"
                aria-label="Usage Guide"
                className="h-8"
              >
                <CircleHelp className="size-4.5" />
              </IconButton>
            </TooltipTrigger>
            <TooltipContent>
              <p>Usage Guide</p>
            </TooltipContent>
          </Tooltip>
        ) : (
          <PopoverButton
            onClick={() => window.open(ZENDESK_HELP_URL, "_blank", "noopener,noreferrer")}
            variant="ghost"
            aria-label="Usage Guide"
            className="h-8 has-[>svg]:px-1.5"
          >
            <CircleHelp className="size-4.5" />
            <span className="text-sm font-normal">Usage Guide</span>
          </PopoverButton>
        )}
      </div>

      {/* Chat History Section - only show when not collapsed */}
      {!isCollapsed && (
        <div className="flex-1 overflow-y-auto px-2 pb-2 scrollbar-subtle">
          {visibleShortcuts.length > 0 && (
            <>
              <div className="px-2 py-1 mb-2">
                <h3 className="text-xs font-medium text-gray-500 tracking-wide">Featured</h3>
              </div>
              <div className="space-y-0.5 mb-2">
                {visibleShortcuts.map((shortcut, index) => (
                  <FeaturedConversationItem key={index} label={shortcut.label} url={shortcut.url} />
                ))}
              </div>
            </>
          )}
          {conversations.length > 0 && (
            <div className="px-2 py-1 mb-2">
              <h3 className="text-xs font-medium text-gray-500 tracking-wide">Recents</h3>
            </div>
          )}
          <div
            role="listbox"
            aria-label="Conversation history"
            {...(focusedItemId ? { "aria-activedescendant": focusedItemId } : {})}
            onKeyDown={handleKeyDown}
            className="space-y-1"
          >
            {conversations.map((conversation, index) => (
              <ConversationItem
                key={conversation.id}
                id={`conversation-${conversation.id}`}
                conversation={conversation}
                isSelected={conversation.id === currentConversationId}
                isFocused={index === focusedIndex}
                isStreaming={conversationsContext?.isStreaming(conversation.id) ?? false}
                onSelect={handleConversationSelect}
                onDelete={onDelete}
                onRename={onRename}
                formatLabel={formatConversationLabel}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
});
